/**
 * Ladder API
 *
 */
import axios from "axios";

// Custom APIError
class APIError extends Error {
    constructor(data) {
        super(data.message);
        this.data = data;
        this.name = "APIError";
    }
}

// Where to store the token
const __LADDER_AUTH_TOKEN = "__LADDER_AUTH_TOKEN";

// API Flags
/* eslint-disable no-unused-vars */
// https://admin.stage.ladder.fit/developer/api-reference#overview
const INVALID_SECURITY_KEY = 1,
    NOT_AUTHENTICATED = 2,
    NOT_AUTHORIZED = 3,
    NOT_LADDER_MEMBER = 4,
    NOT_FOUND = 5,
    INVALID_INPUT = 6,
    UNKOWN_ERROR = 7;

// Create an instance of axios to communicate with Ladder
const http = axios.create({
    baseURL: process.env.VUE_APP_API_BASE,
    headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Security-Key": process.env.VUE_APP_API_KEY
    }
});

// Create a node to use as an event dispatcher
let eventNode = document.createTextNode("");

// Build our base API implementation
let api = {
    off: eventNode.removeEventListener.bind(eventNode),
    on: eventNode.addEventListener.bind(eventNode),
    emit: (type, data) => {
        eventNode.dispatchEvent(new CustomEvent(type, { detail: data }));
    }
};

// Extend our API with methods
api = {
    ...api,
    // Send a request to the API (get by default)
    async call(
        path,
        data = {},
        method = "get",
        files = false,
        progressCallback = false
    ) {
        if (!navigator.onLine) {
            throw new APIError({
                message: "Please check your internet connection and try again"
            });
        }

        let request = {
            url: path,
            method,
            data,
            headers: {
                Authorization: "Bearer " + this.getAuthToken()
            }
        };

        // If we are uploading files, we need to set the content type header
        if (files) {
            request.headers["Content-Type"] = "multipart/form-data";

            // For file uploads we can send events to show progress of upload
            if (progressCallback) {
                request.onUploadProgress = progressCallback;
            }
        }

        try {
            let response = await http.request(request);

            this.handleFlag(response.data.flag);
            return response.data;
        } catch (err) {
            // The error needs to be converted away from an Error object otherwise it won't have data
            JSON.parse(JSON.stringify(err));
            if (err.response) {
                this.handleFlag(err.response.data.flag);
                throw new APIError(err.response.data);
            } else {
                throw new APIError({ message: "Unknown Error" });
            }
        }
    },
    setAuthToken(token) {
        localStorage.setItem(__LADDER_AUTH_TOKEN, token);
        this.emit("auth::update", { token: this.getAuthToken() });
    },
    removeAuthToken() {
        localStorage.removeItem(__LADDER_AUTH_TOKEN);
        this.emit("auth::update", { token: this.getAuthToken() });
    },
    getAuthToken() {
        return localStorage.getItem(__LADDER_AUTH_TOKEN) || null;
    },
    handleFlag(flag) {
        if (
            flag === NOT_AUTHENTICATED ||
            flag === INVALID_SECURITY_KEY ||
            flag === NOT_LADDER_MEMBER
        ) {
            this.removeAuthToken();
        }
    },
    // SHORTCUT METHODS
    // Create a resource
    create(path, data = {}) {
        return this.call(path, data, "post");
    },
    //  Read a resource
    async read(path, data = {}) {
        let response = await this.call(path, data, "get");
        return response.data;
    },
    // Update a resource
    // We override the method to allow for file uploads
    async update(path, data = {}, files = false, progressCallback = false) {
        if (data instanceof FormData) {
            data.append("_method", "PATCH");
        } else {
            data = { ...data, _method: "PATCH" };
        }

        let response = await this.call(
            path,
            data,
            "post",
            files,
            progressCallback
        );

        return response;
    },
    // Replace an entire resource
    async replace(path, data = {}) {
        let response = await this.call(path, data, "put");
        return response;
    },
    // Delete a resource
    async delete(path, data = {}) {
        let response = await this.call(path, data, "delete");
        return response;
    },
    // Account Shortcuts
    // Get the current user object
    async me() {
        let response = await this.read("me");
        return response;
    },
    // Authentication Shortcuts
    // Sign up for an account
    async signUp(account) {
        let response = await this.create("users", account);

        // Save the auth token
        this.setAuthToken(response.data.auth_token);
        return response.data;
    },
    async signIn(email, password) {
        let response = await this.create("authentication", {
            email,
            password
        });

        // Save the auth token
        this.setAuthToken(response.data.auth_token);
        return response.data;
    },
    // Sign out
    async signOut() {
        let response = await this.delete("authentication");
        this.removeAuthToken();
        return response;
    }
};

// Make this a plugin for Vue
const ladderVue = {
    install: Vue => {
        Vue.prototype.$ladder = api;
    }
};

export { ladderVue, api };
